import React from 'react';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {
  Select,
  Option,
  Meta,
} from '@packages/systems/design-system/WebflowSelect';
import type {CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import {useSharedFlowContext, LogicLabel} from '@packages/systems/flows/shared';
import {Note, NoteLink} from '@packages/systems/spring';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import {
  type BlockTypes,
  type Compatibility,
  getCollectionFieldCompatibility,
} from '../utils';
import {rightSidebarWidth} from '@packages/systems/design-system/constants/DesignerUI';

const SelectWrapper = emotionStyled('div')({
  display: 'grid',
  gridRowGap: tokens.space[1],
  marginBottom: tokens.space[1],
});

const OptionLabel = emotionStyled('div')({
  pointerEvents: 'all',
  cursor: 'not-allowed',
});

const TooltipWrapper = emotionStyled('div')({
  width: rightSidebarWidth,
});

/**
 * This renders the list of collections in a dropdown.
 */
export function CollectionSettings({
  onChange,
  value,
  collections,
  blockType,
}: {
  onChange: (arg1: string) => Promise<void>;
  value: null | string;
  collections: CollectionsType;
  blockType: BlockTypes;
}) {
  const {openCMSPanel} = useSharedFlowContext();

  const options = React.useMemo(() => {
    const result: Array<{
      id: string;
      label: string;
      compatibility: Compatibility;
    }> = [];

    for (const [, coll] of collections) {
      result.push({
        id: coll.pluralName,
        label: coll.pluralName,
        compatibility:
          blockType !== 'Delete'
            ? getCollectionFieldCompatibility(coll.fields)
            : 'compatible',
      });
    }

    return result;
  }, [collections, blockType]);

  const currentOption = React.useMemo(() => {
    return options.find((val) => val.id === value);
  }, [value, options]);

  function onCollectionChange(newCollection: null | string, meta: Meta) {
    if (
      meta.changeType === 'changeEnd' &&
      newCollection &&
      newCollection !== value
    ) {
      onChange(newCollection);
    }
  }

  return (
    <>
      <LogicLabel
        label="Collection"
        htmlFor="flow-cms-collection-dropdown"
        required
      />
      <SelectWrapper>
        <TooltipTrigger
          alternate
          placement="left"
          content={
            options.length === 0
              ? 'To select a collection, add a collection in the Designer.'
              : ''
          }
        >
          <Select
            id="flow-cms-collection-dropdown"
            data-automation-id="flow-cms-collection-dropdown"
            disabled={options.length === 0}
            value={value}
            onChange={onCollectionChange}
            placeholder={<span>Select a collection...</span>}
            aria-label={`Collection name: ${value || ''} required`}
          >
            {options.length === 0 ? (
              <Option
                label="Select a collection..."
                data-automation-id="flow-cms-collection-default"
              />
            ) : (
              options.map((option) => {
                const disabled =
                  option.compatibility === 'incompatible-required';

                return (
                  <Option
                    key={`flow-cms-collection-${option.label}`}
                    value={option.id}
                    label={
                      !disabled ? (
                        option.label
                      ) : (
                        <TooltipTrigger
                          placement="left"
                          alternate
                          content={
                            <TooltipWrapper>
                              This collection has required fields that aren‘t
                              compatible with flows.
                            </TooltipWrapper>
                          }
                        >
                          <OptionLabel>{option.label}</OptionLabel>
                        </TooltipTrigger>
                      )
                    }
                    data-automation-id={`flow-cms-collection-${option.label}`}
                    disabled={disabled}
                  />
                );
              })
            )}
          </Select>
        </TooltipTrigger>
      </SelectWrapper>

      {options.length === 0 && (
        <Note colorVariant="default" hasIcon={false}>
          There are no collections to select.
          <NoteLink onClick={openCMSPanel}>Add a collection.</NoteLink>
        </Note>
      )}

      {blockType !== 'Delete' &&
      currentOption &&
      currentOption.compatibility === 'incompatible-optional' ? (
        <Note colorVariant="warning" hasIcon={false}>
          One or more fields in this collection is not compatible with logic
          flows and will not be shown.
        </Note>
      ) : null}
    </>
  );
}
